if (!customElements.get('blog-directory')) {
  customElements.define('blog-directory', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.query = this.querySelector('[data-blog-query]');
      this.topic = this.querySelector('[data-blog-topic]');
      this.topicConfig = JSON.parse(this.querySelector('[data-blog-topic-config]')?.textContent || '{}')[this.dataset.blogHandle];
      if (this.topic && this.topicConfig) {
        this.topic.replaceChildren(new Option(this.dataset.blogHandle === 'customer-stories' ? 'All use cases' : 'All topics', ''), ...this.topicConfig.topics.map(topic => new Option(topic, topic)));
      }
      this.sort = this.querySelector('[data-blog-sort]');
      this.results = this.querySelector('[data-blog-results]');
      this.pagination = this.querySelector('[data-blog-pagination]');
      this.status = this.querySelector('[data-blog-status]');
      this.retry = this.querySelector('[data-blog-retry]');
      this.reset = this.querySelector('[data-blog-reset]');
      this.pageSize = Math.max(4, Number(this.dataset.pageSize) || 12);
      this.blogPath = new URL(this.dataset.blogUrl, location.href).pathname.replace(/\/$/, '');
      this.showDate = this.dataset.showDate !== 'false';
      this.defaultSort = this.dataset.defaultSort === 'title' ? 'title' : 'newest';
      this.revision = 0;
      this.state = this.readState();
      this.syncControls();
      this.setView(this.state.display, false);
      this.querySelector('[data-blog-toolbar]').hidden = false;
      this.querySelector('[data-blog-search]').addEventListener('submit', event => {
        event.preventDefault(); clearTimeout(this.debounce);
        this.state.q = this.query.value.trim(); this.state.page = 1; this.apply();
      }, options);
      this.query.addEventListener('input', () => {
        clearTimeout(this.debounce);
        this.state.q = this.query.value.trim(); this.state.page = 1;
        this.debounce = setTimeout(() => this.apply(), 180);
      }, options);
      for (const [element, key] of [[this.topic, 'tag'], [this.sort, 'sort']]) {
        element?.addEventListener('change', () => {
          clearTimeout(this.debounce); this.state[key] = element.value; this.state.page = 1; this.apply();
        }, options);
      }
      this.querySelectorAll('[data-blog-view]').forEach(button => button.addEventListener('click', () => this.setView(button.dataset.blogView), options));
      this.reset.addEventListener('click', () => this.clearFilters(), options);
      this.retry.addEventListener('click', () => this.apply(), options);
      this.results.addEventListener('click', event => {
        if (event.target.closest('[data-blog-empty-reset]')) this.clearFilters();
      }, options);
      this.pagination.addEventListener('click', event => {
        const link = event.target.closest('a');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault(); clearTimeout(this.debounce);
        this.state.page = Math.max(1, Number(new URL(link.href).searchParams.get('page')) || 1);
        this.apply(true);
      }, options);
      window.addEventListener('popstate', () => {
        clearTimeout(this.debounce); this.state = this.readState(); this.syncControls(); this.setView(this.state.display, false); this.apply();
      }, options);
      this.warmTimer = setTimeout(() => this.ensureIndex().catch(() => {}), 250);
      if (this.state.q || this.state.tag || this.state.sort !== 'newest') this.apply();
    }
    readState() {
      const url = new URL(location.href);
      let initial = [], saved = 'grid';
      try { initial = JSON.parse(this.dataset.initialTags) || []; } catch { /* No tagged route. */ }
      try { saved = localStorage.getItem('tyto.blog.display') || 'grid'; } catch { /* Storage can be disabled. */ }
      const requested = url.searchParams.get('display') || saved;
      const requestedTag = url.searchParams.get('tag') ?? (url.pathname.includes('/tagged/') ? initial[0] || '' : '');
      const tag = [...(this.topic?.options || [])].find(option => this.fold(option.value) === this.fold(requestedTag))?.value || '';
      return {
        q: (url.searchParams.get('q') || '').slice(0, 160).trim(),
        tag,
        sort: ['newest', 'oldest', 'title'].includes(url.searchParams.get('sort')) ? url.searchParams.get('sort') : this.defaultSort,
        page: Math.max(1, Math.floor(Number(url.searchParams.get('page'))) || 1),
        display: requested === 'list' ? 'list' : 'grid'
      };
    }
    syncControls() {
      this.query.value = this.state.q;
      if (this.topic) this.topic.value = this.state.tag;
      this.sort.value = this.state.sort;
      this.reset.hidden = !this.state.q && !this.state.tag && this.state.sort === this.defaultSort;
      [[this.topic, this.state.tag], [this.sort, this.state.sort !== this.defaultSort]].forEach(([element, active]) => element?.setAttribute('data-active', String(Boolean(active))));
    }
    setView(view, update = true) {
      this.state.display = view === 'list' ? 'list' : 'grid';
      this.dataset.view = this.state.display;
      this.querySelectorAll('[data-blog-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.blogView === this.state.display)));
      try { localStorage.setItem('tyto.blog.display', this.state.display); } catch { /* The toggle works without storage. */ }
      if (update) {
        this.updateLocation();
        if (this.filtered) this.renderResults();
      }
    }
    clearFilters() {
      clearTimeout(this.debounce);
      Object.assign(this.state, { q: '', tag: '', sort: this.defaultSort, page: 1 });
      this.syncControls(); this.apply();
    }
    fold(value) {
      return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
    }
    plainText(html) {
      // Template contents are inert: embedded images, frames and scripts never run.
      const template = document.createElement('template');
      template.innerHTML = String(html || '').replace(/<\/(?:p|div|li|h[1-6]|tr|td|section|blockquote)>|<br\s*\/?>/gi, '$& ');
      template.content.querySelectorAll('script,style,noscript,template,svg').forEach(element => element.remove());
      return template.content.textContent.replace(/\s+/g, ' ').trim();
    }
    async readPage(number, signal) {
      const url = new URL(this.dataset.blogUrl, location.href);
      url.searchParams.set('view', 'directory-data'); url.searchParams.set('page', number);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      for (let attempt = 0; attempt < 3; attempt++) {
        const timeout = new AbortController();
        const cancel = () => timeout.abort();
        signal.addEventListener('abort', cancel, { once: true });
        const timer = setTimeout(cancel, 20000);
        try {
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
          const response = await fetch(url, { signal: timeout.signal, credentials: 'same-origin' });
          if ([429, 503].includes(response.status) && attempt < 2) {
            await new Promise(resolve => setTimeout(resolve, 600 * (attempt + 1)));
            continue;
          }
          if (!response.ok) throw new Error('Blog data request failed');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = document.querySelector('[data-blog-directory-index]');
          if (!source) throw new Error('Blog data is unavailable');
          const data = JSON.parse(source.textContent);
          if (data.blog !== this.dataset.blogHandle || !Array.isArray(data.articles) || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 500 || data.page !== number) throw new Error('Invalid blog index');
          return data;
        } finally {
          clearTimeout(timer); signal.removeEventListener('abort', cancel);
        }
      }
    }
    async loadIndex(signal) {
      const first = await this.readPage(1, signal), articles = new Map(first.articles.map(article => [String(article.id), article]));
      let next = 2;
      const worker = async () => {
        while (next <= first.pages) {
          const page = next++, data = await this.readPage(page, signal);
          data.articles.forEach(article => articles.set(String(article.id), article));
          if (this.dataset.loading === 'true') this.status.textContent = `${articles.size} of ${first.total} posts loaded…`;
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, first.pages - 1) }, worker));
      if (articles.size !== first.total) throw new Error('The blog changed while loading; retry for a complete index');
      return [...articles.values()].filter(article => typeof article.url === 'string' && article.url.startsWith(this.blogPath + '/')).map(article => {
        const plain = this.plainText(article.body), tags = Array.isArray(article.tags) ? article.tags : [];
        const topics = this.topicConfig ? [...new Set([...(this.topicConfig.assignments[String(article.id)] || []), ...this.topicConfig.topics.filter(topic => tags.some(tag => this.fold(tag) === this.fold(topic)))])] : tags;
        return { ...article, body: undefined, plain, tags, topics, excerpt: this.plainText(article.excerpt), searchable: this.fold(`${article.title} ${plain} ${tags.join(' ')} ${topics.join(' ')}`), timestamp: Date.parse(article.date) || 0 };
      });
    }
    ensureIndex() {
      if (this.index) return Promise.resolve(this.index);
      if (this.indexPromise) return this.indexPromise;
      const controller = this.controller;
      this.indexPromise = this.loadIndex(controller.signal).then(index => {
        if (this.controller !== controller || controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        this.index = index; this.dataset.indexReady = 'true'; return index;
      }).catch(error => { this.indexPromise = null; throw error; });
      return this.indexPromise;
    }
    async apply(scroll = false) {
      const revision = ++this.revision;
      this.syncControls(); this.retry.hidden = true;
      if (!this.index) { this.dataset.loading = 'true'; this.results.setAttribute('aria-busy', 'true'); this.status.textContent = 'Searching all posts…'; }
      try {
        const index = await this.ensureIndex();
        if (!this.isConnected || revision !== this.revision) return;
        const terms = (this.state.q.match(/"[^"]+"|\S+/g) || []).map(term => this.fold(term.replace(/^"|"$/g, ''))).filter(Boolean);
        const topic = this.fold(this.state.tag);
        this.filtered = index.filter(article => (!topic || article.topics.some(value => this.fold(value) === topic)) && terms.every(term => article.searchable.includes(term)));
        this.filtered.sort(this.state.sort === 'title' ? (a, b) => a.title.localeCompare(b.title, document.documentElement.lang || 'en') : this.state.sort === 'oldest' ? (a, b) => a.timestamp - b.timestamp : (a, b) => b.timestamp - a.timestamp);
        this.state.page = Math.min(this.state.page, Math.max(1, Math.ceil(this.filtered.length / this.pageSize)));
        this.dataset.searching = String(Boolean(this.state.q));
        this.renderResults(); this.updateLocation();
        this.status.textContent = `${this.filtered.length} ${this.filtered.length === 1 ? 'post' : 'posts'}${this.state.q ? ` for “${this.state.q}”` : ''}`;
        if (scroll) {
          const top = this.querySelector('[data-blog-toolbar]').getBoundingClientRect().top + scrollY - 110;
          window.scrollTo({ top: Math.max(0, top), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          this.results.focus({ preventScroll: true });
        }
      } catch (error) {
        if (!this.isConnected || revision !== this.revision || this.controller?.signal.aborted) return;
        this.status.textContent = 'Search could not load. Please try again.';
        this.retry.hidden = false;
      } finally {
        if (revision === this.revision) { this.dataset.loading = 'false'; this.results.removeAttribute('aria-busy'); }
      }
    }
    make(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    excerpt(article) {
      if (!this.state.q) return article.excerpt;
      const term = this.fold(this.state.q.match(/"[^"]+"|\S+/)?.[0]?.replace(/^"|"$/g, '') || '');
      const index = this.fold(article.plain).indexOf(term);
      if (!term || index < 0) return article.excerpt;
      const start = Math.max(0, index - 65), end = Math.min(article.plain.length, start + 200);
      return `${start ? '…' : ''}${article.plain.slice(start, end).trim()}${end < article.plain.length ? '…' : ''}`;
    }
    card(article) {
      const card = this.make('article', 'blog-card');
      if (this.state.display === 'grid') {
        const media = this.make('a', 'blog-card__image'); media.href = article.url; media.tabIndex = -1; media.setAttribute('aria-hidden', 'true');
        let url;
        try { url = new URL(article.image, location.href); if (!article.image || !['https:', 'http:'].includes(url.protocol)) url = null; } catch { url = null; }
        if (url) {
          const image = this.make('img'); image.src = url.href; image.alt = ''; image.loading = 'lazy'; image.decoding = 'async'; image.width = 900; image.height = 506;
          image.srcset = [320, 480, 640, 900].map(width => { const sized = new URL(url); sized.searchParams.set('width', width); return `${sized.href} ${width}w`; }).join(', ');
          image.sizes = '(min-width:1100px) 23vw, (min-width:768px) 30vw, (min-width:540px) 45vw, 90vw'; media.append(image);
        } else {
          media.classList.add('blog-card__placeholder');
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg'), path = document.createElementNS(svg.namespaceURI, 'path');
          svg.setAttribute('viewBox', '0 0 64 64'); svg.setAttribute('width', '56'); svg.setAttribute('height', '56'); svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.5'); path.setAttribute('d', 'M16 8h23l10 10v38H16zM38 8v12h11M23 30h19M23 38h19M23 46h12'); svg.append(path); media.append(svg);
        }
        card.append(media);
      }
      const content = this.make('div', 'blog-card__content'), heading = this.make('h2', 'blog-card__title'), link = this.make('a', '', article.title);
      link.href = article.url; heading.append(link); content.append(heading);
      if (this.showDate) { const date = this.make('time', 'blog-card__date', article.dateLabel); date.dateTime = article.date.slice(0, 10); content.append(date); }
      content.append(this.make('p', 'blog-card__excerpt', this.excerpt(article)));
      const more = this.make('a', 'blog-card__more', 'Read more ›'); more.href = article.url; more.tabIndex = -1; more.setAttribute('aria-hidden', 'true'); content.append(more);
      const corners = this.make('span', 'blog-card__corners'); corners.setAttribute('aria-hidden', 'true'); card.append(content, corners); return card;
    }
    renderResults() {
      const start = (this.state.page - 1) * this.pageSize;
      const cards = this.filtered.slice(start, start + this.pageSize).map(article => this.card(article));
      if (!cards.length) {
        const empty = this.make('div', 'blog-directory__empty');
        empty.append(this.make('h2', '', 'No posts found'), this.make('p', '', 'Try another keyword or clear the filters.'));
        const reset = this.make('button', 'blog-directory__reset', 'Clear search and filters'); reset.type = 'button'; reset.dataset.blogEmptyReset = ''; empty.append(reset); cards.push(empty);
      }
      this.results.replaceChildren(...cards); this.renderPagination();
    }
    stateUrl(page = this.state.page) {
      const url = new URL(location.href); url.pathname = this.blogPath;
      for (const key of ['q', 'tag', 'year', 'sort', 'display', 'page']) url.searchParams.delete(key);
      for (const key of ['q', 'tag']) if (this.state[key]) url.searchParams.set(key, this.state[key]);
      if (this.state.sort !== this.defaultSort) url.searchParams.set('sort', this.state.sort);
      if (this.state.display === 'list') url.searchParams.set('display', 'list');
      if (page > 1) url.searchParams.set('page', page);
      return url;
    }
    updateLocation() {
      history.replaceState(null, '', this.stateUrl());
      this.pagination.querySelectorAll('a').forEach(link => {
        const page = Math.max(1, Number(new URL(link.href).searchParams.get('page')) || 1); link.href = this.stateUrl(page);
      });
    }
    renderPagination() {
      const pages = Math.ceil(this.filtered.length / this.pageSize);
      if (pages < 2) { this.pagination.replaceChildren(); return; }
      const nav = this.make('nav', 'pagination'); nav.setAttribute('aria-label', 'Blog pagination');
      const add = (text, page, label) => { const link = this.make('a', '', text); link.href = this.stateUrl(page); if (label) link.setAttribute('aria-label', label); nav.append(link); };
      if (this.state.page > 1) add('←', this.state.page - 1, 'Previous page');
      const visible = new Set([1, pages]); for (let page = Math.max(1, this.state.page - 2); page <= Math.min(pages, this.state.page + 2); page++) visible.add(page);
      let previous = 0;
      for (const page of [...visible].sort((a, b) => a - b)) {
        if (previous && page - previous > 1) nav.append(this.make('span', '', '…'));
        if (page === this.state.page) { const current = this.make('span', 'current', String(page)); current.setAttribute('aria-current', 'page'); nav.append(current); }
        else add(String(page), page, `Page ${page}`);
        previous = page;
      }
      if (this.state.page < pages) add('→', this.state.page + 1, 'Next page');
      this.pagination.replaceChildren(nav);
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      clearTimeout(this.debounce); clearTimeout(this.warmTimer); this.revision++;
    }
  });
}
